// @section 1 Acquisitions live-data provider.
// Loads the full pipeline tree from /api/hq/acquisitions/pipeline once (with a
// static-fixture fallback) and exposes it through a hook whose shape is
// identical to the old static `acquisitions` object — so the Pipeline board,
// CIM Explorer, and per-deal detail swap a one-line import and otherwise stay
// unchanged. While the live fetch is in flight the static fixture is served so
// the board paints instantly instead of flashing empty; once live data lands it
// replaces the fixture and `source` flips to 'api'.

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  acquisitions as fixtureStore,
  fetchPipeline,
  type AcquisitionsFixture,
  type Deal,
  type WorkflowStage,
} from './acquisitions';

// @section 2 Context shape — mirrors the static `acquisitions` object, plus
// load metadata (source = where the current data came from; loading = first
// fetch still in flight).
export type AcquisitionsStore = {
  asOf: string;
  stages: WorkflowStage[];
  deals: Deal[];
  byId(dealId: string): Deal | undefined;
  stageByKey(stageKey: string): WorkflowStage | undefined;
  source: 'api' | 'fixture';
  loading: boolean;
};

function makeStore(
  data: AcquisitionsFixture,
  source: 'api' | 'fixture',
  loading: boolean,
): AcquisitionsStore {
  return {
    asOf: data.as_of,
    stages: data.workflow_stages,
    deals: data.deals,
    byId: (dealId) => data.deals.find((d) => d.deal_id === dealId),
    stageByKey: (stageKey) => data.workflow_stages.find((s) => s.stage_key === stageKey),
    source,
    loading,
  };
}

// Seed value = the static fixture (instant first paint, loading=true until the
// live fetch resolves).
const FIXTURE_SEED: AcquisitionsFixture = {
  as_of: fixtureStore.asOf,
  workflow_stages: fixtureStore.stages,
  deals: fixtureStore.deals,
};

const AcquisitionsContext = createContext<AcquisitionsStore>(
  makeStore(FIXTURE_SEED, 'fixture', true),
);

// @section 3 Provider — fetches once on mount, swaps to live data when it lands.
export function AcquisitionsProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{
    data: AcquisitionsFixture;
    source: 'api' | 'fixture';
    loading: boolean;
  }>({ data: FIXTURE_SEED, source: 'fixture', loading: true });

  useEffect(() => {
    let alive = true;
    fetchPipeline()
      .then(({ data, source }) => {
        if (alive) setState({ data, source, loading: false });
      })
      .catch(() => {
        if (alive) setState({ data: FIXTURE_SEED, source: 'fixture', loading: false });
      });
    return () => {
      alive = false;
    };
  }, []);

  const store = useMemo(
    () => makeStore(state.data, state.source, state.loading),
    [state],
  );

  return (
    <AcquisitionsContext.Provider value={store}>
      {children}
    </AcquisitionsContext.Provider>
  );
}

// @section 4 Hook — drop-in for the old static `acquisitions` import.
export function useAcquisitions(): AcquisitionsStore {
  return useContext(AcquisitionsContext);
}
